import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  ArrowUpDown,
  Boxes,
  CheckCircle2,
  ChevronDown,
  CircleDot,
  Flag,
  Search,
  SlidersHorizontal,
  Tag,
  User,
  X,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { cn } from '../../lib/utils';
import {
  activeServiceRequestFilterCount,
  APPROVAL_STATUS_OPTIONS,
  CLOSED_OR_PARKED_STAGES,
  SERVICE_REQUEST_ORDER_OPTIONS,
  SERVICE_REQUEST_PRIORITIES,
  SERVICE_REQUEST_STAGES,
  serviceRequestPriorityLabel,
  stageLabel,
  toggleListValue,
  type ImplementationSlim,
  type PersonRef,
  type ServiceRequestListFilters,
} from '../../lib/service-request-views';
import type { ServiceRequestType } from '../../lib/service-requests';
import { useOverlays } from './overlay-context';

const SEARCH_DEBOUNCE_MS = 200;

export type ServiceRequestFilterOptions = {
  types: ServiceRequestType[];
  owners: PersonRef[];
  implementations: ImplementationSlim[];
};

type Props = {
  filters: ServiceRequestListFilters;
  options: ServiceRequestFilterOptions;
  onChange: (patch: Partial<ServiceRequestListFilters>) => void;
  onClear: () => void;
  /** Board only: show the Changes Required / Rejected / Cancelled columns. */
  parked?: { checked: boolean; label: string; onChange: (checked: boolean) => void };
};

function chipClass(active: boolean): string {
  return cn(
    'h-8 gap-1 px-2 font-normal text-muted-foreground',
    active && 'bg-muted font-medium text-foreground',
  );
}

function ChipMenu({
  label,
  count,
  icon: Icon,
  children,
  className,
}: {
  label: string;
  count: number;
  icon: LucideIcon;
  children: ReactNode;
  className?: string;
}) {
  const { pushBlock, popBlock } = useOverlays();
  return (
    <DropdownMenu
      onOpenChange={(open) => {
        if (open) pushBlock();
        else popBlock();
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="ghost" size="sm" className={chipClass(count > 0)}>
          <Icon className="h-3.5 w-3.5" aria-hidden />
          {label}
          {count > 0 ? <span className="font-mono text-xs tabular-nums text-muted-foreground">{count}</span> : null}
          <ChevronDown className="h-3 w-3 opacity-70" aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className={cn('max-h-[70vh] w-56 overflow-y-auto', className)}>
        {children}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Single-value chip backed by a radio-like checkbox list (one selection at a time). */
function SingleChipMenu({
  label,
  icon,
  value,
  options,
  onChange,
}: {
  label: string;
  icon: LucideIcon;
  value: string | null;
  options: Array<{ value: string; label: string }>;
  onChange: (value: string | null) => void;
}) {
  const selected = options.find((option) => option.value === value);
  return (
    <ChipMenu label={selected ? `${label}: ${selected.label}` : label} count={value ? 1 : 0} icon={icon}>
      {options.length === 0 ? (
        <DropdownMenuLabel className="font-normal text-muted-foreground">No options</DropdownMenuLabel>
      ) : null}
      {options.map((option) => (
        <DropdownMenuCheckboxItem
          key={option.value}
          checked={option.value === value}
          onCheckedChange={(checked) => onChange(checked ? option.value : null)}
        >
          <span className="truncate">{option.label}</span>
        </DropdownMenuCheckboxItem>
      ))}
    </ChipMenu>
  );
}

function TextField({
  id,
  label,
  value,
  placeholder,
  onCommit,
  type = 'text',
  min,
  max,
}: {
  id: string;
  label: string;
  value: string;
  placeholder?: string;
  onCommit: (value: string) => void;
  type?: 'text' | 'date' | 'number';
  min?: number;
  max?: number;
}) {
  const [draft, setDraft] = useState(value);
  useEffect(() => {
    setDraft(value);
  }, [value]);
  return (
    <div className="space-y-1">
      <Label htmlFor={id} className="text-xs text-muted-foreground">{label}</Label>
      <Input
        id={id}
        type={type}
        min={min}
        max={max}
        value={draft}
        placeholder={placeholder}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={() => {
          if (draft !== value) onCommit(draft);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            if (draft !== value) onCommit(draft);
          }
        }}
        className="h-8"
      />
    </div>
  );
}

function ToggleRow({
  id,
  label,
  checked,
  onChange,
}: {
  id: string;
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label htmlFor={id} className="flex cursor-pointer items-center gap-2 text-sm">
      <Checkbox id={id} checked={checked} onCheckedChange={(state) => onChange(state === true)} />
      {label}
    </label>
  );
}

export default function ServiceRequestFilters({ filters, options, onChange, onClear, parked }: Props) {
  const { pushBlock, popBlock } = useOverlays();
  const [queryDraft, setQueryDraft] = useState(filters.q ?? '');
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    setQueryDraft(filters.q ?? '');
  }, [filters.q]);

  useEffect(() => {
    const trimmed = queryDraft.trim() || null;
    if (trimmed === (filters.q ?? null)) return;
    const handle = window.setTimeout(() => {
      onChangeRef.current({ q: trimmed, offset: 0 });
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [filters.q, queryDraft]);

  const set = (patch: Partial<ServiceRequestListFilters>) => onChange({ ...patch, offset: 0 });
  const toggle = (key: 'stage' | 'priority' | 'approval_status', value: string) =>
    set({ [key]: toggleListValue(filters[key], value) } as Partial<ServiceRequestListFilters>);

  const openStages = SERVICE_REQUEST_STAGES.filter((stage) => !CLOSED_OR_PARKED_STAGES.includes(stage));
  const moreCount =
    (filters.customer ? 1 : 0) +
    (filters.venue ? 1 : 0) +
    (filters.department ? 1 : 0) +
    (filters.target_after || filters.target_before ? 1 : 0) +
    (filters.readiness_min != null || filters.readiness_max != null ? 1 : 0) +
    (filters.overdue ? 1 : 0) +
    (filters.blocked ? 1 : 0) +
    (filters.at_risk ? 1 : 0) +
    (filters.implementation_id ? 1 : 0);
  const activeCount = activeServiceRequestFilterCount(filters);
  const panelCount = moreCount + filters.stage.length + filters.approval_status.length + (parked?.checked ? 1 : 0);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="relative w-full sm:max-w-xs sm:flex-1">
        <Search
          className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
          aria-hidden
        />
        <Input
          value={queryDraft}
          onChange={(event) => setQueryDraft(event.target.value)}
          placeholder="Search SR number, title, customer…"
          aria-label="Search Service Requests"
          className="h-8 rounded-lg bg-background pl-8 shadow-none"
        />
      </div>

      <ChipMenu label="Priority" count={filters.priority.length} icon={Flag} className="w-44">
        {SERVICE_REQUEST_PRIORITIES.map((priority) => (
          <DropdownMenuCheckboxItem
            key={priority}
            checked={filters.priority.includes(priority)}
            onCheckedChange={() => toggle('priority', priority)}
          >
            {serviceRequestPriorityLabel(priority)}
          </DropdownMenuCheckboxItem>
        ))}
      </ChipMenu>

      <SingleChipMenu
        label="Type"
        icon={Tag}
        value={filters.request_type_id}
        options={options.types.map((type) => ({ value: type.id, label: type.name }))}
        onChange={(value) => set({ request_type_id: value })}
      />

      <SingleChipMenu
        label="Owner"
        icon={User}
        value={filters.owner_id}
        options={options.owners.map((owner) => ({ value: owner.user_id, label: owner.name }))}
        onChange={(value) => set({ owner_id: value })}
      />

      <Popover
        onOpenChange={(open) => {
          if (open) pushBlock();
          else popBlock();
        }}
      >
        <PopoverTrigger asChild>
          <Button type="button" variant="ghost" size="sm" className={chipClass(panelCount > 0)}>
            <SlidersHorizontal className="h-3.5 w-3.5" aria-hidden />
            Filters
            {panelCount > 0 ? <span className="font-mono text-xs tabular-nums text-muted-foreground">{panelCount}</span> : null}
            <ChevronDown className="h-3 w-3 opacity-70" aria-hidden />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="max-h-[75vh] w-[24rem] space-y-4 overflow-y-auto">
          <div className="space-y-1.5">
            <p className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground"><CircleDot className="h-3.5 w-3.5" aria-hidden /> Stage</p>
            <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
              {openStages.map((stage) => (
                <ToggleRow key={stage} id={`sr-filter-stage-${stage}`} label={stageLabel(stage)} checked={filters.stage.includes(stage)} onChange={() => toggle('stage', stage)} />
              ))}
            </div>
            {parked ? (
              <div className="pt-1">
                <ToggleRow id="sr-filter-parked" label={`Show ${parked.label}`} checked={parked.checked} onChange={parked.onChange} />
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 pt-1">
                {CLOSED_OR_PARKED_STAGES.map((stage) => (
                  <ToggleRow key={stage} id={`sr-filter-stage-${stage}`} label={stageLabel(stage)} checked={filters.stage.includes(stage)} onChange={() => toggle('stage', stage)} />
                ))}
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <p className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground"><CheckCircle2 className="h-3.5 w-3.5" aria-hidden /> Approval</p>
            <div className="grid gap-1.5">
              {APPROVAL_STATUS_OPTIONS.map((option) => (
                <ToggleRow key={option.value} id={`sr-filter-approval-${option.value}`} label={option.label} checked={filters.approval_status.includes(option.value)} onChange={() => toggle('approval_status', option.value)} />
              ))}
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <TextField
              id="sr-filter-customer"
              label="Customer"
              value={filters.customer ?? ''}
              placeholder="UCL"
              onCommit={(value) => set({ customer: value.trim() || null })}
            />
            <TextField
              id="sr-filter-venue"
              label="Venue / site"
              value={filters.venue ?? ''}
              onCommit={(value) => set({ venue: value.trim() || null })}
            />
            <TextField
              id="sr-filter-department"
              label="Department"
              value={filters.department ?? ''}
              placeholder="Operations"
              onCommit={(value) => set({ department: value.trim() || null })}
            />
            <div className="space-y-1">
              <Label htmlFor="sr-filter-implementation" className="text-xs text-muted-foreground">Implementation</Label>
              <Select
                value={filters.implementation_id ?? '__any'}
                onValueChange={(value) => set({ implementation_id: value === '__any' ? null : value })}
              >
                <SelectTrigger id="sr-filter-implementation" className="h-8">
                  <SelectValue placeholder="Any" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__any">Any</SelectItem>
                  {options.implementations.map((implementation) => (
                    <SelectItem key={implementation.id} value={implementation.id}>
                      <span className="inline-flex items-center gap-1.5">
                        <Boxes className="h-3 w-3 text-muted-foreground" aria-hidden />
                        {implementation.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <p className="text-xs font-medium text-muted-foreground">Target go-live</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <TextField
                id="sr-filter-target-after"
                label="From"
                type="date"
                value={filters.target_after ?? ''}
                onCommit={(value) => set({ target_after: value || null })}
              />
              <TextField
                id="sr-filter-target-before"
                label="To"
                type="date"
                value={filters.target_before ?? ''}
                onCommit={(value) => set({ target_before: value || null })}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <p className="text-xs font-medium text-muted-foreground">Readiness %</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <TextField
                id="sr-filter-readiness-min"
                label="Min"
                type="number"
                min={0}
                max={100}
                value={filters.readiness_min == null ? '' : String(filters.readiness_min)}
                onCommit={(value) => set({ readiness_min: value === '' ? null : Math.min(100, Math.max(0, Number(value) || 0)) })}
              />
              <TextField
                id="sr-filter-readiness-max"
                label="Max"
                type="number"
                min={0}
                max={100}
                value={filters.readiness_max == null ? '' : String(filters.readiness_max)}
                onCommit={(value) => set({ readiness_max: value === '' ? null : Math.min(100, Math.max(0, Number(value) || 0)) })}
              />
            </div>
          </div>

          <div className="grid gap-2">
            <ToggleRow id="sr-filter-overdue" label="Overdue only" checked={filters.overdue} onChange={(checked) => set({ overdue: checked })} />
            <ToggleRow id="sr-filter-blocked" label="Blocked only" checked={filters.blocked} onChange={(checked) => set({ blocked: checked })} />
            <ToggleRow id="sr-filter-at-risk" label="At risk only" checked={filters.at_risk} onChange={(checked) => set({ at_risk: checked })} />
          </div>
        </PopoverContent>
      </Popover>

      <div className="ml-auto flex min-w-0 flex-wrap items-center justify-end gap-1.5">
        <Select value={filters.order_by} onValueChange={(value) => onChange({ order_by: value, offset: 0 })}>
          <SelectTrigger
            aria-label="Sort"
            className="h-8 w-auto gap-1.5 border-none bg-transparent px-2 text-muted-foreground shadow-none"
          >
            <ArrowUpDown className="h-3.5 w-3.5" aria-hidden />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SERVICE_REQUEST_ORDER_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {activeCount > 0 || parked?.checked ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 shrink-0 font-normal text-muted-foreground"
            onClick={() => {
              setQueryDraft('');
              onClear();
            }}
          >
            <X className="h-3.5 w-3.5" aria-hidden />
            Clear
          </Button>
        ) : null}
      </div>
    </div>
  );
}
